import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AppState } from '../app.service';

@Injectable({
  providedIn: "root",
})
export class FooziAiConciergeService {
  API_URL: string = environment.fooziBaseUrl
  CREATE_SESSION = '/v2/session'
  currentUser: any;
  path: any;
  SEND_MESSAGE = '/v2/session/<session_id>/message'
  RESUME_SESSION = '/v2/session/active'
  GET_SESSION = '/v2/session/<session_id>'
  APP_API_URL = environment.apiBaseUrl
  LIKE_RESPONSE = '/like_response'
  DISLIKE_RESPONSE = '/dislike_response'
  isSessionExpired: BehaviorSubject<number> = new BehaviorSubject(0);
  getDocumentDetails = '/get_document_details_v2'
  microActionCustomize = '/v2/session/<session_id>/action/<scid>/customize'
  microActionGet = '/v2/session/<session_id>/action/<scid>'
  sessionEvent = '/v2/session/<session_id>/event'
  LIST_SESSIONS = '/v2/sessions'
  SUGGESTED_QUESTIONS = '/v2/suggested_questions'
  LIST_CUSTOMIZATIONS = '/v2/customizations'
  /**
   * The conversation the Coach panel is using. Null while the panel is blank — a session is not
   * opened until the first message. Anything holding a session id should follow this rather than
   * reading sessionStorage once and caching it, or it keeps writing to a conversation the learner
   * has already left.
   */
  private activeSession = new BehaviorSubject<string | null>(null);
  private isVisitedFooziComp = new BehaviorSubject<any>(null);
  private isHamburgerExpanded = new BehaviorSubject<any>(null);

  get activeSession$() {
    return this.activeSession.asObservable();
  }
  /** Announces the conversation now in use, or null when the panel has been cleared. */  /** Announces the conversation now in use, or null when the panel has been cleared. */
  setActiveSession(sessionId: string | null) {
    this.activeSession.next(sessionId || null);
  }
  get expandHamburger() {
    return this.isHamburgerExpanded.asObservable();
  }
  get FooziCompStatus() {
    return this.isVisitedFooziComp.asObservable();
  }
  constructor(
    private http: HttpClient,
    private appState: AppState
  ) {}

  /**
   * Opens a session. Without screenContentId that is a general conversation; with one it is that
   * micro-action's customisation thread, which is get-or-create — a second call resumes the first
   * rather than starting a rival thread. The two never resolve to each other.
   *
   * `showDefaultQuestions` settles, once and for the life of the session, whether the panel shows
   * the greeting text and its default questions above this conversation. THIS side decides it — the
   * server stores what it is given, returns it unchanged on every read, and never derives or flips
   * it. Omitted, the server defaults it to true, so the only calls that need to say anything are the
   * ones starting somewhere other than a greeted screen.
   *
   * It is what makes the screen the learner started on a fact about the conversation rather than
   * about this browser: a refresh, a cycle switch, another device and History all read it back the
   * same, with nothing to infer locally.
   */
  public createChatSession(
    screenContentId?: number | string,
    showDefaultQuestions?: boolean,
  ) : Observable<any> {
    this.currentUser = this.appState.get('currentUser')
    this.path = this.appState.get('currentPath')
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    // userId/pathId stay in the form body, as they always have.
    const body = new HttpParams().set('userId', this.currentUser.id).set('pathId', this.path.id);
    // Both are QUERY parameters — POST /session?screen_content_id=X&show_default_questions=false.
    // Sent in the body the anchor is ignored, and the server hands back a plain chat session: a new
    // one on every open, its turns in the learner's conversation, and a row in the Chats tab.
    let params = new HttpParams();
    if (screenContentId != null) {
      params = params.set('screen_content_id', String(screenContentId));
    }
    if (typeof showDefaultQuestions === 'boolean') {
      params = params.set('show_default_questions', String(showDefaultQuestions));
    }
    const options: { headers: HttpHeaders; params?: HttpParams } = { headers };
    if (params.keys().length) { options.params = params; }
    return this.http.post(this.API_URL + this.CREATE_SESSION, body, options);
  }

  /**
   * The starter chips for this learner, without opening a session.
   *
   * Read-only and side-effect free: nothing is created and no history is touched, so it is safe to
   * call every time the panel opens. The list is the same one POST /session would seed a new
   * session with, so chips rendered from it do not change when a session is later minted.
   *
   * Templated from the learner's current step, phase and cycle, so it has to be refetched when the
   * cycle changes. The server answers with a generic set rather than failing when it cannot look the
   * learner up; a transport error still falls through to the caller.
   */
  public getSuggestedQuestions(): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.get(this.API_URL + this.SUGGESTED_QUESTIONS, { headers });
  }

  public checkSessionValidity(session_id) : Observable<any> {
    // const headers = new HttpHeaders();
    // headers.set('Authorization', localStorage.getItem('token'));
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.get(this.API_URL + this.GET_SESSION.replace('<session_id>', session_id), {headers: headers});
  }

  /**
   * Resumes the learner's last conversation. 404 "no active session" is the expected first-use
   * answer, not an error state, and the caller should then open one with POST /session.
   *
   * Chat stream only. A micro-action's thread is resolved by resolveActionSession, whose single
   * get-or-create POST does the same job in one request.
   */
  public checkResumeSessionValidity() : Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.get(this.API_URL + this.RESUME_SESSION, {headers: headers});
  }

  public sendMessage(session_id, message) : Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.post(
      this.API_URL + this.SEND_MESSAGE.replace('<session_id>', session_id),
      { query: message },
      { headers: headers }
    );
  }

  public getPostID(postReplyID) {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    const params = new HttpParams().set('id', postReplyID);
    return this.http.get(this.APP_API_URL + '/getPostRepliesDetails', { headers: headers, params });
  }
  
  public getPostReplies(postID, mode, path_id, reply_id?) {
    const headers = new HttpHeaders();
    const translate =this.appState.get('selectedLanguage')? true:false
    headers.set('Authorization', localStorage.getItem('token'));
    let params = new HttpParams().set('id', postID).set('mode', mode).set('routine_answer_id', 'undefined').set('page', '2').set('path_id', path_id).set('is_translate',translate);
    if (reply_id && reply_id !=undefined) {
      params = new HttpParams().set('id', postID).set('mode', mode).set('routine_answer_id', 'undefined').set('page', '1').set('path_id', path_id).set('reply_id', reply_id).set('is_translate',translate)
    }
    return this.http.get(this.APP_API_URL + '/get_posts_replies', {headers: headers, params: params});
  }


  public sendLike(response, prompt, feedback = '', like = 'true') {
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token'));
    const params = new HttpParams().append('response', response)
                                   .append('prompt', prompt)
                                   .append('feedback', feedback)
                                   .append('like', like);
    return this.http.post(this.APP_API_URL + this.LIKE_RESPONSE, params, {headers: headers});
  }

  /**
   * The Conversations tab, one page at a time. Deliberately light — row metadata only, no message
   * bodies — so a page renders from one call; the transcript is fetched per row via
   * checkSessionValidity() when one is opened, never for every row.
   *
   * message_count and preview count chat turns only, so a session that also holds action turns is
   * not inflated by them. preview is null until there is a user turn with content in it.
   */
  listSessions(limit: number = 20, offset: number = 0): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    // Clamped rather than passed through: a limit outside 1–100 or a negative offset is a 422, and
    // a page request is not worth failing over an off-by-one at the edge of a list.
    const params = new HttpParams()
      .set('limit', String(Math.min(Math.max(limit, 1), 100)))
      .set('offset', String(Math.max(offset, 0)));
    return this.http.get(this.API_URL + this.LIST_SESSIONS, { headers, params });
  }

  /**
   * Names a conversation, or clears the name it was given.
   *
   * Sent as one line and at full length: the server sets the limit, and a name over it is a 422
   * the rename dialog explains while keeping what the learner typed. An empty title clears it, and
   * the row falls back to the preview of its first message.
   *
   * A rename deliberately does not reorder History: the server leaves updated_at alone.
   */
  renameSession(sessionId: string, title: string | null): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    // Sent at full length: the server decides how long a name may be, and says so with a 422.
    const cleaned = (title || '').replace(/[\r\n\t]+/g, ' ').trim();
    return this.http.patch(
      this.API_URL + this.GET_SESSION.replace('<session_id>', sessionId),
      { title: cleaned || null },
      { headers },
    );
  }

  /** Permanently deletes one conversation. 404 when it is not the caller's. */
  deleteSession(sessionId: string): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.delete(this.API_URL + this.GET_SESSION.replace('<session_id>', sessionId), { headers });
  }

  /** Permanently deletes every conversation. scope=all is required, or the call 422s. */
  deleteAllSessions(): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    const params = new HttpParams().set('scope', 'all');
    return this.http.delete(this.API_URL + this.LIST_SESSIONS, { headers, params });
  }

  /**
   * The micro-action customisations the learner has made, newest first — one row per action at its
   * highest version. Omitting screenContentId returns them across every action, which is what the
   * History modal's Micro-actions tab lists.
   */
  listCustomizations(screenContentId?: number | string, limit: number = 20, offset: number = 0): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    let params = new HttpParams()
      .set('limit', String(Math.min(Math.max(limit, 1), 100)))
      .set('offset', String(Math.max(offset, 0)));
    if (screenContentId != null) {
      params = params.set('screen_content_id', String(screenContentId));
    }
    return this.http.get(this.API_URL + this.LIST_CUSTOMIZATIONS, { headers, params });
  }

  /**
   * The session that holds one micro-action's customisation turns.
   *
   * One call: POST /session?screen_content_id=X is get-or-create, so it returns the action's
   * existing thread or opens one. GET /session/active?screen_content_id=X is not used — it answers
   * 404 until a thread exists, which made every first open two requests to learn nothing.
   *
   * Never the chat session. A conversation and a customisation thread are separate streams, and
   * /session/{sid}/action/{scid}/customize answers 422 when handed a session anchored elsewhere —
   * so resolving by "whatever the learner last did on home" both pollutes the conversation and,
   * on the current backend, fails outright.
   */
  resolveActionSession(screenContentId: number | string): Observable<string> {
    // Explicitly false. A customisation thread is never greeted — it opens on the action the learner
    // tapped, not on an invitation to ask something — and the server no longer works that out for
    // itself: omitted, the flag now defaults to TRUE, which would put the intro and its default
    // questions above this thread the first time one is reopened from History.
    return this.createChatSession(screenContentId, false)
      .pipe(map((created: any) => created?.session_id || ''));
  }

  fetchDocumentDetails(docId) {
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token'));
    const params = new HttpParams().set('doc_id', docId)
    return this.http.get(this.APP_API_URL + this.getDocumentDetails, {headers: headers, params: params});
  }
  /**
   * How many Coach panels are on screen.
   *
   * The hamburger hosts one inline and the header opens one as a modal, so "is the panel open" is
   * not a yes/no the app could otherwise answer. Anything that wants to OPEN a panel — rather than
   * use one — asks this first, or the learner gets two of them stacked.
   */
  private mountedPanels = 0;

  /**
   * Asks the panel already on screen to honour a pending open-this-action request.
   *
   * The web layout keeps a panel mounted beside the home screen, so a cycle switch that lands back
   * on the same route never re-creates it and its ngOnInit never runs again. Without this the
   * request would sit in storage until the learner happened to reopen the panel.
   */
  private openPendingAction = new Subject<void>();
  openPendingAction$ = this.openPendingAction.asObservable();

  requestPendingAction(): void { this.openPendingAction.next(); }

  panelMounted(): void { this.mountedPanels += 1; }
  panelUnmounted(): void { this.mountedPanels = Math.max(0, this.mountedPanels - 1); }
  get hasPanelOnScreen(): boolean { return this.mountedPanels > 0; }

  setFooziCompStatus(val) {
    this.isVisitedFooziComp.next(val);
  }
  setExpandHamburger(val) {
    this.isHamburgerExpanded.next(val);
  }
  get sessionValidityStatus() {
    return this.isSessionExpired.asObservable();
  }
  setsessionValidityStatus(val) {
    this.isSessionExpired.next(val);
  }

  customizeMicroAction(
    session_id: string,
    scid: string,
    body: { prompt: string; preset_key?: string }
  ): Observable<any> {
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token') || '');
    return this.http.post(
      this.API_URL + this.microActionCustomize
        .replace('<session_id>', session_id)
        .replace('<scid>', scid),
      body,
      { headers: headers }
    );
  }

  getLatestMicroActionSuggestion(session_id: string, scid: string): Observable<any> {
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token') || '');
    return this.http.get(
      this.API_URL + this.microActionGet
        .replace('<session_id>', session_id)
        .replace('<scid>', scid),
      { headers: headers }
    );
  }

  appendSessionEvent(
    session_id: string,
    body: Record<string, any>
  ): Observable<any> {
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token') || '');
    return this.http.post(
      this.API_URL + this.sessionEvent.replace('<session_id>', session_id),
      body,
      { headers: headers }
    );
  }

  saveActionCustomization(payload: {
    screen_content_id: number | string;
    step_id: number | string;
    screen_id: number | string;
    user_action_completion: boolean;
    title?: string;
    description?: string;
    base_title?: string;
    base_description?: string;
    learning_objective?: any;
    personalization_signals?: any;
  }): Observable<any> {
    const headers = new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
    return this.http.post(
      environment.fooziApiRoRUrl + '/kyper_coach/save_action_customization',
      payload,
      { headers }
    );
  }
}
